import { useEffect, useState, useCallback, useRef } from "react";
import { Reorder, useDragControls, AnimatePresence } from "framer-motion";
import { Loader2, Plus, Trash2, Check, CalendarDays, ListTodo, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const HOME = "#A8C3A0";
const inputCls =
  "w-full px-3 py-2 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200";

function Checkbox({ checked, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors duration-200"
      style={{ borderColor: HOME, backgroundColor: checked ? HOME : "transparent" }}
    >
      {checked && <Check className="w-3.5 h-3.5 text-foreground/80" strokeWidth={3} />}
    </button>
  );
}

function Handle({ controls, testid }) {
  return (
    <button
      type="button"
      onPointerDown={(e) => controls.start(e)}
      data-testid={testid}
      className="cursor-grab active:cursor-grabbing touch-none p-1 text-muted-foreground/50 hover:text-foreground/70 shrink-0"
      aria-label="Drag to reorder"
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
}

function SubtaskRow({ todoId, sub, onChanged, persistOrder }) {
  const controls = useDragControls();

  const toggle = async () => {
    try {
      const { data } = await api.put(`/todos/${todoId}/subtasks/${sub.id}`, { done: !sub.done });
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const edit = async (text) => {
    if (text === sub.text) return;
    try {
      const { data } = await api.put(`/todos/${todoId}/subtasks/${sub.id}`, { text });
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    try {
      const { data } = await api.delete(`/todos/${todoId}/subtasks/${sub.id}`);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Reorder.Item
      value={sub}
      as="div"
      dragListener={false}
      dragControls={controls}
      onDragEnd={persistOrder}
      className="flex items-center gap-1.5 group bg-card"
      data-testid={`subtask-${sub.id}`}
    >
      <Handle controls={controls} testid={`subtask-drag-${sub.id}`} />
      <Checkbox checked={sub.done} onClick={toggle} testid={`subtask-toggle-${sub.id}`} />
      <input
        defaultValue={sub.text}
        onBlur={(e) => edit(e.target.value)}
        data-testid={`subtask-text-${sub.id}`}
        className={`flex-1 bg-transparent border-none focus:outline-none text-sm ${
          sub.done ? "text-muted-foreground" : "text-foreground/90"
        }`}
      />
      <button
        onClick={remove}
        className="p-1 text-muted-foreground/50 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`subtask-delete-${sub.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </Reorder.Item>
  );
}

function TodoCard({ todo, onChanged, onRemoved, persistOrder }) {
  const [newSub, setNewSub] = useState("");
  const controls = useDragControls();
  const { progress } = todo;
  const pct = progress.total ? (progress.completed / progress.total) * 100 : 0;
  const subsRef = useRef(todo.subtasks);
  subsRef.current = todo.subtasks;

  const patchTodo = async (changes) => {
    try {
      const { data } = await api.put(`/todos/${todo.id}`, changes);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reorderSubs = (newSubs) => onChanged({ ...todo, subtasks: newSubs });
  const persistSubOrder = async () => {
    try {
      await api.put(`/todos/${todo.id}/reorder-subtasks`, { ids: subsRef.current.map((s) => s.id) });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addSub = async (e) => {
    e.preventDefault();
    if (!newSub.trim()) return;
    try {
      const { data } = await api.post(`/todos/${todo.id}/subtasks`, { text: newSub.trim() });
      setNewSub("");
      onChanged(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeTodo = async () => {
    try {
      await api.delete(`/todos/${todo.id}`);
      onRemoved(todo.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Reorder.Item
      value={todo}
      as="div"
      dragListener={false}
      dragControls={controls}
      onDragEnd={persistOrder}
      className="rounded-xl border border-border bg-card p-5"
      style={{ borderLeft: `3px solid ${HOME}` }}
      data-testid={`todo-card-${todo.id}`}
    >
      <div className="flex items-start gap-2">
        <Handle controls={controls} testid={`todo-drag-${todo.id}`} />
        <div className="pt-0.5">
          <Checkbox checked={todo.done} onClick={() => patchTodo({ done: !todo.done })} testid={`todo-done-${todo.id}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className={`font-serif text-xl leading-snug ${todo.done ? "text-muted-foreground" : "text-foreground"}`}>
              {todo.title}
            </h3>
            <button onClick={removeTodo} className="p-1.5 text-muted-foreground hover:text-destructive shrink-0" data-testid={`todo-delete-${todo.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {progress.total > 0 && (
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${HOME}33`, color: "#2C2C2C" }}
                data-testid={`todo-progress-${todo.id}`}
              >
                {progress.completed}/{progress.total} done
              </span>
            )}
            {todo.due_date && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarDays className="w-3 h-3" /> {fmtDate(todo.due_date)}
              </span>
            )}
          </div>

          {todo.note && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{todo.note}</p>}

          {progress.total > 0 && (
            <div className="h-1.5 rounded-full bg-secondary mt-3 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: HOME }} />
            </div>
          )}

          {/* subtasks (draggable) */}
          {todo.subtasks.length > 0 && (
            <Reorder.Group axis="y" values={todo.subtasks} onReorder={reorderSubs} className="mt-4 space-y-1.5" as="div">
              {todo.subtasks.map((sub) => (
                <SubtaskRow key={sub.id} todoId={todo.id} sub={sub} onChanged={onChanged} persistOrder={persistSubOrder} />
              ))}
            </Reorder.Group>
          )}

          <form onSubmit={addSub} className="flex items-center gap-2 mt-3">
            <input
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              placeholder="Add a sub-task…"
              data-testid={`subtask-new-${todo.id}`}
              className={inputCls}
            />
            <button type="submit" className="p-2 rounded-md hover:bg-secondary text-foreground/70" data-testid={`subtask-add-${todo.id}`}>
              <Plus className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </Reorder.Item>
  );
}

export default function TodosPage() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const todosRef = useRef([]);
  todosRef.current = todos;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pillars/home/todos");
      setTodos(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persistTodoOrder = async () => {
    try {
      await api.put("/pillars/home/todos/reorder", { ids: todosRef.current.map((t) => t.id) });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addTodo = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/pillars/home/todos", {
        title: title.trim(),
        due_date: due || null,
        note: note.trim() || null,
      });
      setTodos((t) => [...t, data]);
      setTitle("");
      setDue("");
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const onChanged = (updated) => setTodos((t) => t.map((x) => (x.id === updated.id ? updated : x)));
  const onRemoved = (id) => setTodos((t) => t.filter((x) => x.id !== id));

  return (
    <div className="max-w-3xl px-6 md:px-12 py-10 md:py-14" data-testid="todos-page">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: HOME }} />
        <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Home</span>
      </div>
      <h1 className="font-serif text-3xl md:text-4xl text-foreground flex items-center gap-3 mb-2">
        <ListTodo className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} /> To-Do
      </h1>
      <p className="text-sm text-muted-foreground mb-8">Drag the handle to reorder todos and sub-tasks.</p>

      {/* Add todo */}
      <form onSubmit={addTodo} className="rounded-xl border border-border bg-card p-5 mb-8 space-y-3" data-testid="todo-add-form">
        <div className="flex gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" data-testid="todo-new-title" className={inputCls} />
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="todo-new-due" className="w-[160px] px-2 py-2 rounded-md border border-input bg-white/70 text-sm text-muted-foreground focus:outline-none focus:border-foreground/40" />
        </div>
        <div className="flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" data-testid="todo-new-note" className={inputCls} />
          <button
            type="submit"
            disabled={adding || !title.trim()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-background font-medium disabled:opacity-50 whitespace-nowrap"
            style={{ backgroundColor: HOME, color: "#2C2C2C" }}
            data-testid="todo-add-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to-do
          </button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : todos.length === 0 ? (
        <p className="text-muted-foreground italic" data-testid="todos-empty">No to-dos yet. Add your first above.</p>
      ) : (
        <Reorder.Group axis="y" values={todos} onReorder={setTodos} className="space-y-4" as="div">
          {todos.map((todo) => (
            <TodoCard key={todo.id} todo={todo} onChanged={onChanged} onRemoved={onRemoved} persistOrder={persistTodoOrder} />
          ))}
        </Reorder.Group>
      )}
    </div>
  );
}
